import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import CareRichTextEditor from "../../components/care/CareRichTextEditor";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { careService } from "../../services/careService";
import {
  assessmentRecordSlug,
  processRecords,
  processResidentName,
  recordLabel,
  recordSlug,
} from "./AssessmentsPage";

const meetAndGreetQuestions = [
  "What daily routines make you feel most comfortable?",
  "What support do you need with transfers, walking, bathing, dressing, or toileting?",
  "What medications, allergies, or health concerns should the care team understand?",
  "What behaviors, triggers, or calming approaches should caregivers know?",
  "Who should be contacted first when care needs change?",
  "What activities, foods, faith practices, hobbies, or preferences matter to you?",
];

const signedDocumentOptions = [
  "Admission agreement",
  "Resident rights and responsibilities",
  "Medication management consent",
  "HIPAA/privacy acknowledgment",
  "House policies",
  "Emergency contact authorization",
  "Financial agreement/payment policy",
  "Care service agreement",
];

const defaultCareCircleMembers = [
  ["Elaine Johnson", "Daughter / PoA"],
  ["Robert Johnson", "Son"],
  ["Maya Torres", "Care taker"],
  ["Linda Green", "Guardian contact"],
];

const defaultCareProviderStaff = [
  ["Sarah Mitchell", "Lead caregiver"],
  ["Daniel Reed", "Care aide"],
];

const splitPersonRole = (member) => {
  const [name, ...relationship] = String(member || "").split(" - ");
  return [name || "", relationship.join(" - ") || ""];
};

export default function AssessmentRecordEditPage() {
  const { assessmentId, recordId } = useParams();
  const [assessment, setAssessment] = useState(null);
  const [record, setRecord] = useState(null);
  const [recordName, setRecordName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const isMeetAndGreet = recordId === "meet-and-greet";
  const isClientOnboarding = recordId === "client-onboarding";
  const isCareMeetingRecord = isMeetAndGreet || isClientOnboarding;
  const [notes, setNotes] = useState(record?.notes || "");
  const [customQuestions, setCustomQuestions] = useState([]);
  const [customQuestion, setCustomQuestion] = useState("");
  const [customDocuments, setCustomDocuments] = useState([]);
  const [customDocument, setCustomDocument] = useState("");
  const [careCircleMembers, setCareCircleMembers] = useState(
    record?.careCircle?.length ? record.careCircle.map(splitPersonRole) : defaultCareCircleMembers,
  );
  const [careProviderStaffMembers, setCareProviderStaffMembers] = useState(
    record?.careProviderStaff?.length ? record.careProviderStaff.map(splitPersonRole) : defaultCareProviderStaff,
  );
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const savedQuestions = new Map((record?.questions || []).map(([question, answer]) => [question, answer]));
  const savedSignedDocuments = new Set(record?.signedDocuments || []);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    Promise.all([
      careService.assessmentProcesses.get(assessmentId),
      careService.assessmentRecords.byProcessAndType(assessmentId, recordId),
    ])
      .then(([process, recordData]) => {
        if (!isMounted) return;
        const matchedRecord = processRecords(process).find((item) => recordSlug(item) === recordId || assessmentRecordSlug(recordLabel(item)) === recordId);
        setAssessment(process || null);
        setRecord(recordData || {});
        setRecordName(recordData?.name || recordData?.title || recordLabel(matchedRecord) || recordId);
        setError("");
      })
      .catch((err) => {
        if (!isMounted) return;
        setAssessment(null);
        setRecord(null);
        setRecordName("");
        setError(err?.response?.data?.message || err?.message || "Unable to load assessment record.");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [assessmentId, recordId]);

  useEffect(() => {
    if (!record) return;
    setNotes(record.notes || "");
    setCareCircleMembers(record.careCircle?.length ? record.careCircle.map(splitPersonRole) : defaultCareCircleMembers);
    setCareProviderStaffMembers(record.careProviderStaff?.length ? record.careProviderStaff.map(splitPersonRole) : defaultCareProviderStaff);
  }, [record]);

  if (loading) {
    return (
      <CareShell className="dashboard-content residents-page assessments-page">
        <Panel title="Assessment Record">Loading assessment record...</Panel>
      </CareShell>
    );
  }
  if (error) {
    return (
      <CareShell className="dashboard-content residents-page assessments-page">
        <div className="residents-actions">
          <Link className="mini-btn" to={`/assessments/${assessmentId}`}><i className="ti ti-arrow-left" /> Assessment</Link>
        </div>
        <p className="form-error">{error}</p>
      </CareShell>
    );
  }
  if (!assessment) return <Navigate to="/assessments" replace />;
  if (!recordName) return <Navigate to={`/assessments/${assessment.id}`} replace />;
  if (!record) return <Navigate to={`/assessments/${assessment.id}/records/${recordId}`} replace />;

  const addCustomQuestion = () => {
    const nextQuestion = customQuestion.trim();
    if (!nextQuestion) return;
    setCustomQuestions((current) => [...current, nextQuestion]);
    setCustomQuestion("");
  };

  const addCustomDocument = () => {
    const nextDocument = customDocument.trim();
    if (!nextDocument) return;
    setCustomDocuments((current) => [...current, nextDocument]);
    setCustomDocument("");
  };

  const addCustomCareCircleMember = () => {
    setCareCircleMembers((current) => [...current, ["", ""]]);
  };

  const removeCareCircleMember = (indexToRemove) => {
    setCareCircleMembers((current) => current.filter((_, index) => index !== indexToRemove));
  };

  const addCareProviderStaffMember = () => {
    setCareProviderStaffMembers((current) => [...current, ["", ""]]);
  };

  const removeCareProviderStaffMember = (indexToRemove) => {
    setCareProviderStaffMembers((current) => current.filter((_, index) => index !== indexToRemove));
  };

  const addUploadedDocuments = (event) => {
    const files = Array.from(event.target.files || []).map((file) => ({ fileName: file.name, name: file.name }));
    if (!files.length) return;
    setUploadedDocuments((current) => [...current, ...files]);
    event.target.value = "";
  };

  const updateUploadedDocumentName = (indexToUpdate, name) => {
    setUploadedDocuments((current) =>
      current.map((document, index) => (index === indexToUpdate ? { ...document, name } : document)),
    );
  };

  const removeUploadedDocument = (indexToRemove) => {
    setUploadedDocuments((current) => current.filter((_, index) => index !== indexToRemove));
  };

  return (
    <CareShell className="dashboard-content residents-page assessments-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>Edit {recordName}</h1>
          <p>{processResidentName(assessment)} - {assessment.name || assessment.title || "Assessment process"}</p>
        </div>
        <div className="residents-actions">
          <Link className="mini-btn" to={`/assessments/${assessment.id}/records/${recordId}`}>
            <i className="ti ti-arrow-left" /> {recordName}
          </Link>
        </div>
      </div>

      <Panel title={isCareMeetingRecord ? `${recordName} Details` : "Assessment Record Details"}>
        <form className="assessment-page-form assessment-edit-form">
          {isCareMeetingRecord ? (
            <div className="assessment-mag-edit-grid">
              <div className="assessment-mag-edit-main">
                <section className="assessment-drawer-section">
                  <h3>{isClientOnboarding ? "Onboarding Details" : "Meeting Details"}</h3>
                  <div className="assessment-drawer-grid">
                    <label className="ncp-form-field">
                      <span>{isClientOnboarding ? "Onboarding date" : "Meeting date"}</span>
                      <input defaultValue={record.meetingDate} name="meetingDate" type="date" />
                    </label>
                    <label className="ncp-form-field">
                      <span>Location</span>
                      <input defaultValue={record.location} name="location" />
                    </label>
                    <label className="ncp-form-field">
                      <span>Social worker</span>
                      <input defaultValue={record.socialWorker} name="socialWorker" />
                    </label>
                    <label className="ncp-form-field">
                      <span>Facilitator</span>
                      <input defaultValue={record.facilitator} name="facilitator" />
                    </label>
                    <div className="ncp-form-field-wide">
                      <span className="assessment-inline-label">Present care provider staff members</span>
                      <div className="assessment-care-circle-form-list">
                        {careProviderStaffMembers.map(([name, role], index) => (
                          <div className="assessment-care-circle-row" key={`staff-${name}-${role}-${index}`}>
                            <label className="ncp-form-field">
                              <span>Person name</span>
                              <input defaultValue={name} name={`staffName-${index}`} placeholder="Full name" />
                            </label>
                            <label className="ncp-form-field">
                              <span>Role</span>
                              <input defaultValue={role} name={`staffRole-${index}`} placeholder="Role present" />
                            </label>
                            <button className="assessment-remove-row" type="button" onClick={() => removeCareProviderStaffMember(index)}>
                              <i className="ti ti-x" /> Remove
                            </button>
                          </div>
                        ))}
                      </div>
                      <button className="mini-btn assessment-add-row" type="button" onClick={addCareProviderStaffMember}>
                        <i className="ti ti-plus" /> Add staff member
                      </button>
                    </div>
                </div>
              </section>

                <section className="assessment-drawer-section">
                  <h3>Care Circle</h3>
                  <div className="assessment-care-circle-form-list">
                    {careCircleMembers.map(([name, relationship], index) => (
                      <div className="assessment-care-circle-row" key={`${name}-${relationship}-${index}`}>
                        <label className="ncp-form-field">
                          <span>Person name</span>
                          <input defaultValue={name} name={`careCircleName-${index}`} placeholder="Full name" />
                        </label>
                        <label className="ncp-form-field">
                          <span>Relationship</span>
                          <input defaultValue={relationship} name={`careCircleRelationship-${index}`} placeholder="Relationship to resident" />
                        </label>
                        <button className="assessment-remove-row" type="button" onClick={() => removeCareCircleMember(index)}>
                          <i className="ti ti-x" /> Remove
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="assessment-custom-question">
                    <button className="mini-btn" type="button" onClick={addCustomCareCircleMember}>
                      <i className="ti ti-plus" /> Add member
                    </button>
                  </div>
                </section>

                <section className="assessment-drawer-section">
                  <div className="assessment-section-heading-row">
                    <h3>Questions and Responses</h3>
                    <span>{meetAndGreetQuestions.length + customQuestions.length} questions</span>
                  </div>
                  <div className="assessment-question-form-list">
                    {[...meetAndGreetQuestions, ...customQuestions].map((question) => (
                      <label className="ncp-form-field ncp-form-field-wide" key={question}>
                        <span>{question}</span>
                        <textarea defaultValue={savedQuestions.get(question) || ""} name={`question-${question}`} rows={2} />
                      </label>
                    ))}
                  </div>
                  <div className="assessment-custom-question">
                    <input value={customQuestion} placeholder="Add custom question" onChange={(event) => setCustomQuestion(event.target.value)} />
                    <button className="mini-btn" type="button" onClick={addCustomQuestion}>
                      <i className="ti ti-plus" /> Add question
                    </button>
                  </div>
                </section>
              </div>

              <aside className="assessment-mag-notes-column assessment-mag-notes-panel">
                <section className="assessment-drawer-section">
                  <h3>Notes</h3>
                  <CareRichTextEditor height={360} onChange={setNotes} value={notes} />
                  <input name="notes" readOnly type="hidden" value={notes} />
                </section>
                <section className="assessment-drawer-section">
                  <h3>Signed Documents</h3>
                  <div className="assessment-document-checklist">
                    {[...signedDocumentOptions, ...customDocuments].map((document) => (
                      <label key={document}>
                        <input defaultChecked={savedSignedDocuments.has(document)} name={`document-${document}`} type="checkbox" />
                        <span>{document}</span>
                      </label>
                    ))}
                  </div>
                  <div className="assessment-custom-question">
                    <input value={customDocument} placeholder="Add custom signed document" onChange={(event) => setCustomDocument(event.target.value)} />
                    <button className="mini-btn" type="button" onClick={addCustomDocument}>
                      <i className="ti ti-plus" /> Add document
                    </button>
                  </div>
                </section>
                <section className="assessment-drawer-section">
                  <h3>Upload Documents</h3>
                  {record.uploadedDocuments?.length ? (
                    <div className="assessment-uploaded-doc-list">
                      {record.uploadedDocuments.map((document) => (
                        <span key={document}>
                          <span className="assessment-attachment-thumb document"><i className="ti ti-file-description" /></span>
                          <strong>{document}</strong>
                          <span className="assessment-document-actions">
                            <button aria-label={`View ${document}`} type="button"><i className="ti ti-eye" /></button>
                            <button aria-label={`Download ${document}`} type="button"><i className="ti ti-download" /></button>
                          </span>
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <label className="assessment-upload assessment-upload-compact">
                    <input className="assessment-file-input" multiple name="uploadedDocuments" onChange={addUploadedDocuments} type="file" />
                    <i className="ti ti-file-upload" />
                    <strong>Attach documents</strong>
                    <small>Upload PDF, Word, images, or care files related to this record.</small>
                  </label>
                  {uploadedDocuments.length ? (
                    <div className="assessment-uploaded-doc-list">
                      {uploadedDocuments.map((document, index) => (
                        <div className="assessment-uploaded-doc-row" key={`${document.fileName}-${index}`}>
                          <span className="assessment-attachment-thumb document"><i className="ti ti-file-description" /></span>
                          <div>
                            <strong>{document.fileName}</strong>
                            <label className="ncp-form-field">
                              <span>Document name</span>
                              <input
                                name={`uploadedDocumentName-${index}`}
                                onChange={(event) => updateUploadedDocumentName(index, event.target.value)}
                                placeholder="Document name"
                                value={document.name}
                              />
                            </label>
                          </div>
                          <button type="button" onClick={() => removeUploadedDocument(index)}><i className="ti ti-x" /></button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </section>
              </aside>
            </div>
          ) : (
            <>
              <section className="assessment-drawer-section">
                <h3>Document</h3>
                <div className="assessment-current-document">
                  <i className="ti ti-file-description" />
                  <div>
                    <span>Current document</span>
                    <strong>{record.document}</strong>
                  </div>
                </div>
                <div className="assessment-drawer-grid">
                  <label className="ncp-form-field ncp-form-field-wide">
                    <span>Replace document</span>
                    <input name="document" type="file" />
                  </label>
                  <label className="ncp-form-field">
                    <span>Social worker</span>
                    <input defaultValue={record.socialWorker} name="socialWorker" />
                  </label>
                  <label className="ncp-form-field">
                    <span>Issuer</span>
                    <input defaultValue={record.issuer} name="issuer" />
                  </label>
                </div>
              </section>

              <section className="assessment-drawer-section">
                <h3>Notes</h3>
                <CareRichTextEditor height={280} onChange={setNotes} value={notes} />
                <input name="notes" readOnly type="hidden" value={notes} />
              </section>
            </>
          )}

          <div className="assessment-drawer-actions">
            <Link className="btn secondary" to={`/assessments/${assessment.id}/records/${recordId}`}>
              Cancel
            </Link>
            <button className="btn primary" type="button">
              Save Changes
            </button>
          </div>
        </form>
      </Panel>
    </CareShell>
  );
}
